import type { DBCore, DBCoreMutateRequest, DBCoreTable, Middleware } from 'dexie';

/**
 * 攔截所有 Dexie 寫入，供 SQLite 鏡像使用（desktop-app.md 6.1）。
 *
 * 用 DBCore middleware 而不是改 9 個 repository：
 * 「前端照舊寫 Dexie，43 處 useLiveQuery 全部保留，15 個直接呼叫 db.<table> 的檔案
 * 全部不動」是這一版的前提，一個攔截點就能全部涵蓋。
 *
 * **只記下哪些 id 髒了，不記下當時的值。** 兩個理由：
 *
 * 1. `mutate` 在交易 commit 之前就 resolve。記值的話，一個之後被回滾的交易
 *    會在鏡像留下一列 Dexie 根本沒有的資料。改成事後重讀，回滾的那筆
 *    重讀出來就是舊值（或不存在），鏡像自然跟著對。
 * 2. 同一筆在一個交易裡被改好幾次時，重讀只會送最後的狀態，不必送三次。
 */

export interface DirtyKey {
  table: string;
  id: string;
}

export interface MirrorObserver {
  /** 有資料列被新增、修改或刪除。 */
  onDirty: (keys: DirtyKey[]) => void;
  /**
   * 發生了無法逐筆表達的批次刪除（`table.clear()`、`deleteRange`）。
   * 這種情況一律整個重建，逐筆同步會留下舊資料殘骸。
   */
  onBulkClear: () => void;
}

function idsOf(request: DBCoreMutateRequest): string[] {
  if (request.type === 'add' || request.type === 'put') {
    const values = (request.values ?? []) as Array<{ id?: unknown }>;
    return values.map((value) => value?.id).filter((id): id is string => typeof id === 'string');
  }
  if (request.type === 'delete') {
    return (request.keys ?? []).filter((key): key is string => typeof key === 'string');
  }
  return [];
}

export function createMirrorMiddleware(observer: MirrorObserver): Middleware<DBCore> {
  return {
    stack: 'dbcore',
    name: 'gentletask-mirror',
    create(downlevel: DBCore): DBCore {
      return {
        ...downlevel,
        table(tableName: string): DBCoreTable {
          const table = downlevel.table(tableName);
          return {
            ...table,
            mutate(request) {
              // **絕對不能 await**。把 `table.mutate` 包進 async function 會讓
              // 回傳值變成原生 Promise，脫離 Dexie 的交易 zone，
              // 交易就會提早 commit（PrematureCommitError）。
              // 所以在呼叫下層之前先同步記下來，然後原封不動把 promise 傳回去。
              //
              // 記在前面也表示「寫失敗的那幾筆也會進髒清單」，但因為之後是
              // 重讀而不是用當時的值，重讀出來就是舊值，鏡像仍然是對的。
              if (request.type === 'deleteRange') {
                observer.onBulkClear();
              } else {
                const ids = idsOf(request);
                if (ids.length > 0) {
                  observer.onDirty(ids.map((id) => ({ table: tableName, id })));
                }
              }

              return table.mutate(request);
            },
          };
        },
      };
    },
  };
}
